import { useEffect, useRef, useState } from "react";
import type { DNAReference } from "../../domain/index.js";
import { useCompositionStore } from "../../stores/compositionStore.js";
import * as api from "../../infra/api.js";
import { Button } from "../../ui/Button.js";

type Status = "idle" | "uploading" | "error";

/**
 * A reference whose DNA has not been analyzed yet — the reducer seeds an
 * `"upload"` reference with the `bpm:0, key:"?", mood:"analyzing"` placeholder
 * until `ReferenceAnalyzed` fills it in (spec §4.6).
 */
function isAnalyzing(ref: DNAReference): boolean {
  return ref.bpm <= 0 || ref.key === "?";
}

/** Tone of the role/source badge: the active reference reads as `butter`. */
function badgeLabel(ref: DNAReference): string {
  if (ref.source === "mashup") return "mashup";
  return ref.role;
}

/**
 * The "Reference DNA" panel — one card per `state.dnaReferences` item, plus the
 * upload control and, when ≥2 references exist, the "blend two references"
 * control (spec §9.3). The panel REFLECTS the materialized `AudioState`:
 * `dnaReferences` arrives over SSE and `compositionStore` re-derives it through
 * the reducer; this component never mutates state. Uploading hits the atomic
 * `POST /songs/:id/references` endpoint; blending dispatches a `POST /commands`
 * that the LLM maps to `BlendReferences`.
 */
export function DnaReference(): JSX.Element {
  const projectId = useCompositionStore((s) => s.projectId);
  const references = useCompositionStore((s) => s.audioState.dnaReferences);
  const inputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);

  const hasReferences = references.length > 0;
  // The first clip uploaded is `primary`; later clips come in as `secondary`
  // so the active reference is not silently swapped out from under the user.
  const nextRole: "primary" | "secondary" = hasReferences ? "secondary" : "primary";

  async function onUpload(file: File): Promise<void> {
    const pid = useCompositionStore.getState().projectId;
    if (!pid) return;
    setStatus("uploading");
    setError(null);
    try {
      // One atomic call: writes the asset, appends `ReferenceUploaded`, enqueues
      // the `dna-analysis` job. The placeholder card appears via SSE.
      await api.uploadReference(pid, file, nextRole);
      setStatus("idle");
    } catch (e) {
      setStatus("error");
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <section className="flex flex-col gap-3" aria-label="Reference DNA">
      <div className="flex items-center justify-between border-b border-border pb-2">
        <p className="catalog-num">DNA DE REFERÊNCIA · SOA COMO ISSO</p>
        <span className="font-mono text-[10px] uppercase tracking-widest2 text-ink-dim">
          {hasReferences
            ? `${references.length} ${references.length === 1 ? "referência" : "referências"}`
            : "○ nenhuma"}
        </span>
      </div>

      <div className="border border-border bg-tape p-5 shadow-sleeve">
        {hasReferences ? (
          <ul className="flex flex-col gap-2" aria-label="Lista de referências de DNA">
            {references.map((ref) => (
              <ReferenceCard key={ref.id} reference={ref} projectId={projectId} />
            ))}
          </ul>
        ) : (
          <div className="flex flex-col items-start gap-2 py-2">
            <p className="font-mono text-[10px] leading-relaxed text-ink-dim">
              Suba um clipe de áudio: a IA mede BPM, tom e mood e gera a música nessa vibe. Nenhuma
              referência ainda.
            </p>
          </div>
        )}

        {references.length >= 2 ? <BlendControl references={references} /> : null}

        <div className="mt-4 flex items-center gap-3 border-t border-border pt-3">
          <Button
            variant="secondary"
            onClick={() => inputRef.current?.click()}
            disabled={!projectId || status === "uploading"}
          >
            {status === "uploading"
              ? "Enviando…"
              : hasReferences
                ? "+ Outra referência"
                : "Subir referência"}
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept="audio/*"
            className="hidden"
            aria-label="DNA reference file"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onUpload(file);
              // Reset so re-selecting the same file fires `change` again.
              e.target.value = "";
            }}
          />
          {hasReferences ? (
            <span className="font-mono text-[10px] leading-relaxed text-ink-dim">
              A última subida vira a referência ativa.
            </span>
          ) : null}
        </div>

        {status === "error" && error ? (
          <p
            role="alert"
            className="mt-3 border border-vinyl/40 bg-vinyl/5 p-2 font-mono text-[10px] uppercase tracking-widest2 text-vinyl"
          >
            {error}
          </p>
        ) : null}
      </div>
    </section>
  );
}

/** One reference: label, the BPM/tom/mood readout (or the analyzing state). */
function ReferenceCard({
  reference,
  projectId,
}: {
  reference: DNAReference;
  projectId: string | null;
}): JSX.Element {
  const analyzing = isAnalyzing(reference);
  const active = reference.role === "primary";
  const [removing, setRemoving] = useState(false);
  // #12: inline (non-blocking) confirmation. Two buttons appear in the card
  // when `confirming === true`; `window.confirm` would freeze the page thread.
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onRemove(): Promise<void> {
    if (!projectId || removing) return;
    // Capture `reference.id` in the closure at the moment of "Confirmar"
    // (#12) — if the user opens another card's confirm prompt between clicks
    // we still delete the right one. `setRemoving` blocks a double-fire.
    const referenceId = reference.id;
    setConfirming(false);
    setRemoving(true);
    setError(null);
    try {
      // Append-only remove: the panel re-renders when the new event arrives via
      // SSE and the reducer drops this entry from `state.dnaReferences`.
      await api.removeReference(projectId, referenceId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      // #8: always reset the spinner, even on success — if the SSE event lags,
      // the user would otherwise stay stuck on "Removendo…" forever.
      setRemoving(false);
    }
  }

  return (
    <li
      className={`flex items-center gap-3 border bg-asphalt px-3 py-2.5 ${
        active ? "border-butter/60" : "border-border"
      }`}
    >
      {/* A spinning-disc glyph that goes solid butter once analysis lands. */}
      <span
        aria-hidden="true"
        className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full border ${
          analyzing
            ? "animate-pulse border-oscillo bg-oscillo/30"
            : active
              ? "border-butter bg-butter"
              : "border-borderHi bg-transparent"
        }`}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium text-paper">{reference.label}</span>
          <Badge analyzing={analyzing} active={active}>
            {badgeLabel(reference)}
          </Badge>
        </div>
        {analyzing ? (
          <span className="font-mono text-[10px] uppercase tracking-widest2 text-oscillo">
            analisando…
          </span>
        ) : (
          <span className="font-mono text-[11px] tabular-nums text-ink-muted">
            {reference.bpm} BPM · Tom {reference.key} · {reference.mood}
          </span>
        )}
        {error ? (
          <p
            role="alert"
            className="mt-1 font-mono text-[10px] uppercase tracking-widest2 text-vinyl"
          >
            {error}
          </p>
        ) : null}
      </div>
      {confirming ? (
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => void onRemove()}
            disabled={!projectId || removing}
            aria-label={`Confirmar remoção da referência ${reference.label}`}
            className="border border-vinyl bg-vinyl/10 px-2 py-1 font-mono text-[10px] uppercase tracking-widest2 text-vinyl hover:bg-vinyl/20 disabled:opacity-50"
          >
            {removing ? "Removendo…" : "Confirmar"}
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            disabled={removing}
            aria-label={`Cancelar remoção da referência ${reference.label}`}
            className="border border-border bg-asphalt px-2 py-1 font-mono text-[10px] uppercase tracking-widest2 text-ink-dim hover:border-borderHi hover:text-paper disabled:opacity-50"
          >
            Cancelar
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          disabled={!projectId || removing}
          aria-label={`Remover referência ${reference.label}`}
          className="shrink-0 border border-border bg-asphalt px-2 py-1 font-mono text-[10px] uppercase tracking-widest2 text-ink-dim hover:border-vinyl hover:text-vinyl disabled:opacity-50"
        >
          {removing ? "Removendo…" : "Remover"}
        </button>
      )}
    </li>
  );
}

/**
 * Inline badge — `butter` for the active (`primary`) reference, `oscillo` while
 * analyzing, neutral for a `secondary` reference. Not the shared `ui/Badge`
 * because that one has no `oscillo` tone for the analyzing state.
 */
function Badge({
  children,
  analyzing,
  active,
}: {
  children: string;
  analyzing: boolean;
  active: boolean;
}): JSX.Element {
  const tone = analyzing
    ? "border-oscillo text-oscillo"
    : active
      ? "border-butter text-butter"
      : "border-border text-ink-muted";
  return (
    <span
      className={`inline-flex items-center border px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest2 ${tone}`}
    >
      {children}
    </span>
  );
}

/**
 * "Mesclar duas referências" — two selectors + a button, shown only when there
 * are ≥2 references. It calls the TYPED `POST /songs/:id/references/blend`
 * endpoint with the two real `referenceId`s (spec §9.3). The old free-text
 * `POST /commands` path could not disambiguate two references with the same
 * label; routing by id removes that ambiguity (F10).
 */
function BlendControl({ references }: { references: DNAReference[] }): JSX.Element {
  const projectId = useCompositionStore((s) => s.projectId);
  const [idA, setIdA] = useState<string>(() => references[0]?.id ?? "");
  const [idB, setIdB] = useState<string>(() => references[1]?.id ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // #5: when a `ReferenceRemoved` event arrives via SSE, the removed id may
  // still be sitting in `idA`/`idB` (selectors initialized from a now-stale
  // references list). Reconcile: if the selected id is no longer present in
  // `references`, fall back to the first / second remaining reference. This
  // mirrors the initial-state seeding and keeps the typed blend POST from
  // firing with a dead `referenceId`.
  useEffect(() => {
    const ids = new Set(references.map((r) => r.id));
    if (idA !== "" && !ids.has(idA)) setIdA(references[0]?.id ?? "");
    if (idB !== "" && !ids.has(idB)) setIdB(references[1]?.id ?? "");
  }, [references, idA, idB]);

  const sameRef = idA === idB;
  const canBlend = Boolean(projectId) && !sameRef && idA !== "" && idB !== "";

  async function onBlend(): Promise<void> {
    const pid = useCompositionStore.getState().projectId;
    if (!canBlend || !pid) return;
    setBusy(true);
    setError(null);
    try {
      // Typed path: the two reference ids go straight to the blend endpoint;
      // the server runs `blendDnaReferences` and appends a `ReferenceUploaded`
      // of `source:"mashup"`. The new mashup card arrives via SSE.
      await api.blendReferences(pid, idA, idB);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 flex flex-col gap-2 border-t border-border pt-3">
      <p className="catalog-num">MESCLAR DUAS REFERÊNCIAS</p>
      <p className="font-mono text-[10px] leading-relaxed text-ink-dim">
        Funde o DNA de duas referências num mashup. BPM é a média; o tom vem da primeira.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <ReferenceSelect
          label="Primeira referência"
          value={idA}
          references={references}
          onChange={setIdA}
        />
        <span aria-hidden="true" className="font-mono text-xs text-ink-dim">
          ×
        </span>
        <ReferenceSelect
          label="Segunda referência"
          value={idB}
          references={references}
          onChange={setIdB}
        />
        <Button
          variant="primary"
          disabled={!canBlend || busy}
          onClick={() => void onBlend()}
          aria-label="Mesclar referências"
        >
          {busy ? "Mesclando…" : "Mesclar"}
        </Button>
      </div>
      {sameRef ? (
        <span className="font-mono text-[10px] uppercase tracking-widest2 text-ink-dim">
          escolha duas referências distintas
        </span>
      ) : null}
      {error ? (
        <p
          role="alert"
          className="border border-vinyl/40 bg-vinyl/5 p-2 font-mono text-[10px] uppercase tracking-widest2 text-vinyl"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

function ReferenceSelect({
  label,
  value,
  references,
  onChange,
}: {
  label: string;
  value: string;
  references: DNAReference[];
  onChange: (id: string) => void;
}): JSX.Element {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="border border-border bg-asphalt px-3 py-1.5 font-mono text-[11px] text-paper focus:border-paper focus:outline-none"
    >
      {references.map((ref) => (
        <option key={ref.id} value={ref.id}>
          {ref.label}
        </option>
      ))}
    </select>
  );
}
